import { useEffect, useState } from 'react';

import { Modal } from '../../components';
import { useQltClassNoteStore } from '../../stores/qlt';
import { EMPTY_CHECKLIST, toggleItem, type ChecklistText } from './checklistText';
import { CLASS_COMMENT_RULE, CLASS_MARK_BANDS, bandById, bandIdOf, commentPayload, commentPresetsFor, hydrateCommentText, tagsForBand } from './classNoteFields';
import '../../styles/class-management.css';

// the box holds the generated sentence as well as the teacher's words
const TEXT_LIMIT = 400;

export { CLASS_COMMENT_PRESETS, CLASS_MARK_BANDS, classMarkLabel } from './classNoteFields';
export type { ClassMarkBand } from './classNoteFields';

export interface ClassLogbookEditorProps {
  open: boolean;
  periodLabel: string;
  subjectTitle?: string;
  lessonName?: string;
  onClose: () => void;
  onSaved: () => void;
}

export function ClassLogbookEditor({ open, periodLabel, subjectTitle, lessonName, onClose, onSaved }: ClassLogbookEditorProps) {
  const note = useQltClassNoteStore((state) => state.currentNote);
  const loading = useQltClassNoteStore((state) => state.loading);
  const error = useQltClassNoteStore((state) => state.error);
  const saveData = useQltClassNoteStore((state) => state.saveData);
  const [band, setBand] = useState('');
  const [comment, setComment] = useState<ChecklistText>(EMPTY_CHECKLIST);

  useEffect(() => {
    if (!open) return;
    setBand(bandIdOf(note?.data));
    setComment(hydrateCommentText(note?.data));
  }, [note, open]);

  function pickBand(next: string) {
    setBand(next);
    // comments the new band does not offer are unticked, and so leave the box
    setComment((current) => {
      const kept = tagsForBand(current.ticks, next);
      return current.ticks.filter((tag) => kept.indexOf(tag) < 0).reduce((state, tag) => toggleItem(state, CLASS_COMMENT_RULE, tag), current);
    });
  }

  function toggleTag(value: string) {
    setComment((current) => toggleItem(current, CLASS_COMMENT_RULE, value));
  }

  async function save() {
    const chosen = bandById(band);
    const updated = await saveData({ classMark: chosen ? chosen.mark : undefined, classMarkBand: band, ...commentPayload(comment.ticks, comment.text) });
    if (updated) {
      onSaved();
      onClose();
    }
  }

  const presets = commentPresetsFor(band);
  // a stored tag outside the band's list stays visible until the teacher changes the band
  const options = presets.concat(comment.ticks.filter((tag) => presets.indexOf(tag) < 0));
  const description = `${periodLabel} • Môn ${subjectTitle || 'học'}${lessonName ? `  |  Nội dung:  ${lessonName}` : ''}`;
  const actions = <button type="button" className="cm-btn-primary" disabled={loading || !note} onClick={() => void save()}>{loading ? 'Đang lưu…' : 'Xác nhận ghi sổ'}</button>;

  return (
    <Modal open={open} title="Đánh giá tiết học" description={description} className="cm-modal cm-modal--meta" actions={actions} onClose={onClose}>
      <div className="cm-label">Đánh giá lớp</div>
      <div className="cm-eval-grid" role="radiogroup" aria-label="Đánh giá lớp">
        {CLASS_MARK_BANDS.map((item) => (
          <button
            key={item.id}
            type="button"
            role="radio"
            aria-checked={band === item.id}
            className={`cm-eval-chip cm-eval-chip--${item.id}${band === item.id ? ' is-on' : ''}`}
            onClick={() => pickBand(band === item.id ? '' : item.id)}
          >
            <span className="cm-eval-chip__box" aria-hidden={true}>✓</span>
            <span className="cm-eval-chip__badge">{item.badge}</span>
            <span className="cm-eval-chip__label">{item.label}</span>
          </button>
        ))}
      </div>
      <div className="cm-label">Nhận xét lớp</div>
      <div className="cm-checklist" role="group" aria-label="Nhận xét lớp">
        {options.map((item) => (
          <label className={comment.ticks.indexOf(item) >= 0 ? 'cm-check is-on' : 'cm-check'} key={item}>
            <input type="checkbox" checked={comment.ticks.indexOf(item) >= 0} onChange={() => toggleTag(item)} />
            <span>{item}</span>
          </label>
        ))}
      </div>
      <label className="cm-label" htmlFor="class-comment-text">Nội dung ghi sổ</label>
      <div className="cm-note">
        <textarea id="class-comment-text" value={comment.text} maxLength={TEXT_LIMIT} onChange={(event) => setComment({ ...comment, text: event.target.value })} placeholder="Chọn nhận xét ở trên hoặc nhập nội dung..." />
        <span className="cm-note__count">{comment.text.length}/{TEXT_LIMIT}</span>
      </div>
      {error ? <p className="cm-error" role="alert">{error}</p> : null}
    </Modal>
  );
}
